import React, { useState, useRef, useEffect } from "react";
import { Drawer } from "vaul";
import { ChevronDown, Check } from "lucide-react";
import type { UserExchange } from "@crypto-trading/common/browser";

interface ExchangeSelectProps {
  exchanges: UserExchange[];
  selectedExchangeCode: string | undefined;
  supportedExchangeCodes: Set<string>;
  isLoading?: boolean;
  onSelect: (exchangeCode: string) => void;
  disabled?: boolean;
  variant?: "drawer" | "popup";
}

export const ExchangeSelect: React.FC<ExchangeSelectProps> = ({
  exchanges,
  selectedExchangeCode,
  supportedExchangeCodes,
  isLoading = false,
  onSelect,
  disabled = false,
  variant = "drawer",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const selectedExchange = exchanges.find(
    (exchange) => exchange.code === selectedExchangeCode
  );

  const formattedBalance = selectedExchange
    ? `${selectedExchange.balance?.free?.toFixed(2) ?? "0.00"} USDT`
    : "--";

  const handleSelect = (exchangeCode: string) => {
    if (!supportedExchangeCodes.has(exchangeCode)) {
      return;
    }
    onSelect(exchangeCode);
    setIsOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (variant !== "popup" || !isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, variant]);

  // Render exchange list items
  const renderExchangeList = () => {
    if (exchanges.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-text-secondary text-sm">No exchanges configured</p>
        </div>
      );
    }

    return (
      <div className="space-y-2">
        {exchanges.map((exchange) => {
          const supported = supportedExchangeCodes.has(exchange.code);
          const isSelected = exchange.code === selectedExchangeCode;
          const balance = `${exchange.balance?.free?.toFixed(2) ?? "0.00"} USDT`;

          const getButtonClassName = () => {
            if (isSelected) return "bg-accent/20 border-accent/50";
            if (supported)
              return "bg-primary/40 border-gray-600 hover:bg-primary/60";
            return "bg-primary/20 border-gray-700 opacity-50 cursor-not-allowed";
          };

          return (
            <button
              key={exchange.id}
              type="button"
              onClick={() => handleSelect(exchange.code)}
              disabled={!supported}
              className={`w-full p-4 rounded-lg border transition-colors text-left ${getButtonClassName()}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-text-primary">
                      {exchange.name}
                    </span>
                    {isSelected && <Check className="w-4 h-4 text-accent" />}
                    {!supported && (
                      <span className="text-xs text-text-secondary bg-black/50 px-2 py-0.5 rounded">
                        Unsupported
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary">
                    Balance: {balance}
                  </p>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    );
  };

  // Popup variant (Desktop)
  if (variant === "popup") {
    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
          disabled={disabled || isLoading || exchanges.length === 0}
          className="w-full bg-primary/40 border border-gray-600 text-text-primary rounded-lg p-2 text-sm focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-between"
        >
          <span className="flex-1 text-left">
            {(() => {
              if (isLoading) return "Loading exchanges...";
              if (selectedExchange)
                return `${selectedExchange.name} (${formattedBalance})`;
              return "Select exchange";
            })()}
          </span>
          <ChevronDown
            className={`w-4 h-4 text-text-secondary flex-shrink-0 ml-2 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className="absolute top-full mt-2 left-0 right-0 bg-secondary border border-gray-500/30 rounded-lg shadow-2xl z-50 max-h-[400px] overflow-hidden flex flex-col"
          >
            <div className="border-b border-gray-500/30 px-4 py-3 bg-secondary/80 backdrop-blur-sm sticky top-0 z-10">
              <h2 className="text-sm font-semibold text-text-primary">
                Select Exchange
              </h2>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {renderExchangeList()}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Drawer variant (Mobile)
  return (
    <Drawer.NestedRoot open={isOpen} onOpenChange={setIsOpen}>
      <button
        type="button"
        onClick={() => !disabled && !isLoading && setIsOpen(true)}
        disabled={disabled || isLoading || exchanges.length === 0}
        className="w-full bg-primary/40 border border-gray-600 text-text-primary rounded-lg p-2 text-sm focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-between"
      >
        <span className="flex-1 text-left">
          {(() => {
            if (isLoading) return "Loading exchanges...";
            if (selectedExchange)
              return `${selectedExchange.name} (${formattedBalance})`;
            return "Select exchange";
          })()}
        </span>
        <ChevronDown className="w-4 h-4 text-text-secondary flex-shrink-0 ml-2" />
      </button>

      <Drawer.Portal>
        <Drawer.Overlay className="fixed inset-0 bg-black/80 z-[130]" />
        <Drawer.Content className="bg-[#1a1b20] flex rounded-t-xl flex-col fixed bottom-0 left-0 right-0 max-h-[80vh] z-[131]">
          <div className="mx-4 border-b border-gray-500/30 pb-3 pt-2 sticky top-0 bg-secondary z-10">
            <Drawer.Handle className="h-1 w-1/3 mt-0 mb-3 rounded-full" />
            <h2 className="text-sm font-semibold text-text-primary text-center">
              Select Exchange
            </h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {renderExchangeList()}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.NestedRoot>
  );
};
